import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import AdminLayout from "@/components/admin/AdminLayout"
// 💡 API 통신 함수 import 주석 해제!
import { getAdminNotice, createAdminNotice, updateAdminNotice, deleteAdminNotice } from "@/api/adminNoticeApi"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"

export default function AdminNoticeFormPage() {
  const navigate = useNavigate()
  const { noticeId } = useParams()
  const isEditMode = noticeId && noticeId !== "new"

  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    isPinned: false, // UI 체크박스용
    visibleYn: "Y"
  })

  useEffect(() => {
    if (isEditMode) {
      fetchNoticeDetail(Number(noticeId))
    }
  }, [isEditMode, noticeId])

  const fetchNoticeDetail = async (id: number) => {
    try {
      setLoading(true)
      const response = await getAdminNotice(id)
      const data = response.data || response // ApiResponse 래핑 고려

      setFormData({
        title: data.title,
        content: data.content,
        isPinned: data.isPinned === 'Y', // 💡 DB의 'Y'/'N'을 체크박스(true/false)로 변환
        visibleYn: data.visibleYn
      })
    } catch (error) {
      alert("공지사항 정보를 불러오지 못했습니다.")
      navigate("/admin/cs/notices")
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement
    const checked = (e.target as HTMLInputElement).checked

    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }))
  }

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      alert("제목과 내용을 모두 입력해주세요.")
      return
    }

    try {
      setLoading(true)

      // 💡 서버에 보낼 때는 체크박스(true/false)를 다시 'Y'/'N'으로 변환해서 전송
      const payload = {
        title: formData.title,
        content: formData.content,
        isPinned: formData.isPinned ? "Y" : "N",
        visibleYn: formData.visibleYn
      }

      if (isEditMode) {
        await updateAdminNotice(Number(noticeId), payload)
        alert("수정되었습니다.")
      } else {
        await createAdminNotice(payload)
        alert("등록되었습니다.")
      }
      navigate("/admin/cs/notices")
    } catch (error) {
      console.error(error)
      alert("저장에 실패했습니다.")
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!confirm("정말 이 공지사항을 삭제하시겠습니까?")) return

    try {
      await deleteAdminNotice(Number(noticeId))
      alert("삭제되었습니다.")
      navigate("/admin/cs/notices")
    } catch (error) {
      console.error(error)
      alert("삭제에 실패했습니다.")
    }
  }

  return (
    <AdminLayout>
      <div className="max-w-4xl space-y-6">
        <div className="flex items-end justify-between">
          <div>
            <h2 className="text-3xl font-bold">공지사항 {isEditMode ? "상세/수정" : "등록"}</h2>
            <p className="mt-2 text-sm text-slate-500">고객에게 안내할 중요 사항을 작성합니다.</p>
          </div>
          {isEditMode && (
            <Button variant="destructive" className="rounded-xl h-11 px-5" onClick={handleDelete}>
              삭제하기
            </Button>
          )}
        </div>

        <Card className="rounded-[28px] border border-slate-200 shadow-sm">
          <CardContent className="p-8 space-y-6">
            {/* 설정 영역 */}
            <div className="flex gap-6 pb-6 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <label className="font-medium text-sm text-slate-700">노출 여부</label>
                <select
                  name="visibleYn"
                  value={formData.visibleYn}
                  onChange={handleChange}
                  className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none"
                >
                  <option value="Y">노출함 (Y)</option>
                  <option value="N">숨김 (N)</option>
                </select>
              </div>

              <div className="flex items-center gap-3">
                <label className="font-medium text-sm text-slate-700 cursor-pointer flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="isPinned"
                    checked={formData.isPinned}
                    onChange={handleChange}
                    className="w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                  />
                  상단 고정 (중요 공지)
                </label>
              </div>
            </div>

            {/* 제목 및 내용 */}
            <div className="space-y-2">
              <label className="font-medium text-sm text-slate-700">제목</label>
              <Input
                name="title"
                placeholder="공지사항 제목을 입력하세요."
                className="rounded-xl h-12"
                value={formData.title}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <label className="font-medium text-sm text-slate-700">내용</label>
              <textarea
                name="content"
                placeholder="공지사항 내용을 입력하세요."
                className="w-full h-80 rounded-xl border border-slate-200 bg-white p-4 text-sm outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400 resize-none"
                value={formData.content}
                onChange={handleChange}
              />
            </div>

            {/* 버튼 영역 */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
              <Button
                variant="outline"
                className="rounded-xl h-12 px-6"
                onClick={() => navigate("/admin/cs/notices")}
              >
                취소
              </Button>
              <Button
                className="rounded-xl bg-slate-900 hover:bg-slate-800 h-12 px-8"
                onClick={handleSave}
                disabled={loading}
              >
                {isEditMode ? "수정완료" : "등록완료"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  )
}